import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Target, Users, TrendingUp, CheckCircle2, Brain, Award, Search, Compass, Rocket } from 'lucide-react';

const Services = () => {
  const services = [
    {
      id: 'org-effectiveness',
      icon: Building2,
      title: 'Organisational Effectiveness',
      tagline: 'Designing structures, governance, and culture that scale.',
      description: 'Designing culture, structure, governance, and capability to support scale and execution.',
      whatWeDesign: [
        'Org structure & job architecture',
        'Culture definition & values activation',
        'Governance & RACI clarity',
        'Competency frameworks',
        'M&A integration & synergy mapping',
        'DEI & inclusive leadership',
      ],
      outcomes: [
        'Faster decisions',
        'Scale-ready structures',
        'Cultural alignment',
      ],
      image: 'https://customer-assets.emergentagent.com/job_1549dae8-750d-40d4-95b1-202e48d84d83/artifacts/hjz8it7h_Image%209%20-%201%20-%20Services%20-Organisational%20Effectiveness.jpg',
      color: 'from-blue-500 to-blue-600'
    },
    {
      id: 'due-diligence',
      icon: Target,
      title: 'Pre-Investment HR Due Diligence',
      tagline: 'Context-driven assessment of people and leadership risk.',
      description: 'Context-driven assessment of people and leadership risk across growth-stage investments.',
      howWeAssess: [
        'Context Lens: Stage, scale, market positioning, growth ambition, competitive intensity',
        'Fit-Based Risk Review: Leadership, org design, talent strategy, culture, ESOPs, hiring engine',
        'Context x Risk Matrix: Context sensitivity x investment impact',
        'Context-Anchored Outputs: Not what is weak - but what breaks next',
      ],
      outcomes: [
        'Clear people risk insight',
        'Fewer surprises',
        'Actionable post-deal agenda',
        'Faster IC decisions',
      ],
      image: 'https://customer-assets.emergentagent.com/job_1549dae8-750d-40d4-95b1-202e48d84d83/artifacts/zg2qnim3_Image%2010%20-%20Services%20-%20Pre-Investment%20HR%20Due%20Diligence.jpg',
      color: 'from-emerald-500 to-emerald-600'
    },
    {
      id: 'fractional-chro',
      icon: TrendingUp,
      title: 'Fractional CHRO Services',
      tagline: 'Senior HR leadership on demand.',
      description: 'Senior HR leadership on demand - strategic depth without full-time commitment.',
      whatWeProvide: [
        '3-year people strategy',
        'Hire-to-fire processes & performance philosophy',
        'Talent, succession & workforce planning',
        'HR operating model & governance cadence',
      ],
      engagementModel: '1-3 days/week | Milestone-based | Enterprise depth without overhead',
      outcomes: [
        'Strategic HR presence',
        'Lower execution risk',
        'Faster people decisions',
        'Cost-effective leadership',
      ],
      image: 'https://customer-assets.emergentagent.com/job_1549dae8-750d-40d4-95b1-202e48d84d83/artifacts/ygfxygrx_Image%2011%20-%20Services%20-%20Fractional%20CHRO%20Services.jpg',
      color: 'from-amber-500 to-amber-600'
    },
    {
      id: 'leadership-search',
      icon: Users,
      title: 'CXO & Critical Leadership Search',
      tagline: 'Leadership hiring aligned to strategy and stage.',
      description: 'Leadership hiring aligned to strategy, stage, and scale ambition.',
      howWeHire: [
        'Outcome-led role definition',
        'Context-aware talent mapping (competitors, adjacencies, selective left-field leaders)',
        'Structured, outcome-based evaluation & culture fit',
        'De-risked closure with stakeholder alignment',
      ],
      outcomes: [
        'Faster impact',
        'Stronger cultural fit',
        'Lower leadership risk',
        'Better execution readiness',
      ],
      image: 'https://customer-assets.emergentagent.com/job_1549dae8-750d-40d4-95b1-202e48d84d83/artifacts/y8o40i5y_Image%2012%20-%20Services%20-%20CXO%20%26%20Critical%20Leadership%20Search.jpg',
      color: 'from-purple-500 to-purple-600'
    },
    {
      id: 'hr-technology',
      icon: Brain,
      title: 'HR Technology Advisory (AI-Enabled)',
      tagline: 'AI-enabled platforms for decision-grade insight.',
      description: 'AI-enabled HR platforms that reduce dependency and deliver decision-grade insight.',
      ourSolution: [
        'AI-enabled platforms and digital agents delivering:',
        '~50% operational reduction',
        '3x faster people decisions',
        'Decision-grade workforce insights',
      ],
      focusAreas: [
        'AI recruitment',
        'Performance analytics',
        'Learning platforms',
        'HR helpdesk agents',
      ],
      outcomes: [
        'Reduced admin burden',
        'Better people insights',
        'Faster decision-making',
        'Strategic HR capacity',
      ],
      image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=800&q=80',
      color: 'from-rose-500 to-rose-600'
    },
    {
      id: 'manager-capability',
      icon: Award,
      title: 'Manager Capability & Coaching',
      tagline: 'Strengthening leadership depth and execution.',
      description: 'Targeted coaching and development to strengthen leadership depth and execution.',
      whatWeDeliver: [
        'ICF-PCC certified executive coaching',
        'Leadership readiness & gravitas assessments',
        'Manager development journeys',
        'Assessment & development centres',
        'Manager community building',
      ],
      howWeDoIt: [
        'Competency-based frameworks',
        'Crucial conversations workshops',
        '360-degree feedback',
        'Emotional intelligence & bias mitigation',
      ],
      outcomes: [
        'Stronger leadership bench',
        'Higher engagement',
        'Faster execution',
        'Improved retention & diversity',
      ],
      image: 'https://customer-assets.emergentagent.com/job_1549dae8-750d-40d4-95b1-202e48d84d83/artifacts/7gp9n0l0_Image%2013%20-%20Services%20-%20Manager%20Capability%20%26%20Coaching.jpg',
      color: 'from-cyan-500 to-cyan-600'
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] pt-32 pb-24 lg:pt-44 lg:pb-32 overflow-hidden bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1560179707-f14e90ef3623?auto=format&fit=crop&w=2073&q=80"
            alt="Modern corporate building"
            className="w-full h-full object-cover opacity-15"
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-8 leading-tight tracking-tight">
              Six Areas Through Which We Drive
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-500">
                Organisational Readiness
              </span>
            </h1>
            <p className="text-xl lg:text-2xl max-w-3xl font-light mb-12 text-slate-300">
              Built to assess, de-risk, and strengthen the people and leadership foundations behind growth.
            </p>
          </div>
        </div>
      </section>

      {/* Services Detail Sections */}
      {services.map((service, index) => {
        const Icon = service.icon;
        const isEven = index % 2 === 0;

        return (
          <section
            key={service.id}
            id={service.id}
            className={`py-20 lg:py-28 ${isEven ? 'bg-white' : 'bg-slate-100'}`}
          >
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className={`grid lg:grid-cols-2 gap-16 items-start ${!isEven ? 'lg:grid-flow-dense' : ''}`}>
                {/* Content */}
                <div className={!isEven ? 'lg:col-start-2' : ''}>
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 bg-gradient-to-r ${service.color}`}>
                    <Icon className="text-white" size={28} />
                  </div>
                  <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4 leading-tight">
                    {service.title}
                  </h2>
                  <p className="text-xl text-amber-600 mb-6 font-medium">{service.tagline}</p>
                  <p className="text-lg text-slate-600 mb-8 leading-relaxed">{service.description}</p>

                  {/* What We Design / Provide / Deliver */}
                  {service.whatWeDesign && (
                    <div className="mb-8">
                      <h3 className="text-lg font-bold text-slate-900 mb-4">What we design</h3>
                      <div className="space-y-3">
                        {service.whatWeDesign.map((item, idx) => (
                          <div key={idx} className="flex items-start space-x-3">
                            <CheckCircle2 className="text-amber-500 flex-shrink-0 mt-0.5" size={18} />
                            <p className="text-slate-600 text-base leading-relaxed">{item}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {service.howWeAssess && (
                    <div className="mb-8">
                      <h3 className="text-lg font-bold text-slate-900 mb-4">How we assess</h3>
                      <div className="space-y-3">
                        {service.howWeAssess.map((item, idx) => (
                          <div key={idx} className="flex items-start space-x-3">
                            <CheckCircle2 className="text-amber-500 flex-shrink-0 mt-0.5" size={18} />
                            <p className="text-slate-600 text-base leading-relaxed">{item}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {service.whatWeProvide && (
                    <div className="mb-8">
                      <h3 className="text-lg font-bold text-slate-900 mb-4">What we provide</h3>
                      <div className="space-y-3">
                        {service.whatWeProvide.map((item, idx) => (
                          <div key={idx} className="flex items-start space-x-3">
                            <CheckCircle2 className="text-amber-500 flex-shrink-0 mt-0.5" size={18} />
                            <p className="text-slate-600 text-base leading-relaxed">{item}</p>
                          </div>
                        ))}
                      </div>
                      {service.engagementModel && (
                        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-amber-700 text-sm font-semibold">Engagement Model:</p>
                          <p className="text-slate-700 text-base mt-1">{service.engagementModel}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {service.howWeHire && (
                    <div className="mb-8">
                      <h3 className="text-lg font-bold text-slate-900 mb-4">How we hire</h3>
                      <div className="space-y-3">
                        {service.howWeHire.map((item, idx) => (
                          <div key={idx} className="flex items-start space-x-3">
                            <CheckCircle2 className="text-amber-500 flex-shrink-0 mt-0.5" size={18} />
                            <p className="text-slate-600 text-base leading-relaxed">{item}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {service.ourSolution && (
                    <div className="mb-8">
                      <h3 className="text-lg font-bold text-slate-900 mb-4">Our solution</h3>
                      <div className="space-y-3">
                        {service.ourSolution.map((item, idx) => (
                          <div key={idx} className="flex items-start space-x-3">
                            <CheckCircle2 className="text-amber-500 flex-shrink-0 mt-0.5" size={18} />
                            <p className="text-slate-600 text-base leading-relaxed">{item}</p>
                          </div>
                        ))}
                      </div>
                      {service.focusAreas && (
                        <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                          <p className="text-slate-700 text-sm font-semibold mb-2">Focus Areas:</p>
                          <div className="flex flex-wrap gap-2">
                            {service.focusAreas.map((area, idx) => (
                              <span key={idx} className="px-3 py-1 bg-white border border-slate-200 text-slate-600 text-sm rounded-full">
                                {area}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {service.whatWeDeliver && (
                    <div className="mb-8">
                      <h3 className="text-lg font-bold text-slate-900 mb-4">What we deliver</h3>
                      <div className="space-y-3">
                        {service.whatWeDeliver.map((item, idx) => (
                          <div key={idx} className="flex items-start space-x-3">
                            <CheckCircle2 className="text-amber-500 flex-shrink-0 mt-0.5" size={18} />
                            <p className="text-slate-600 text-base leading-relaxed">{item}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {service.howWeDoIt && (
                    <div className="mb-8">
                      <h3 className="text-lg font-bold text-slate-900 mb-4">How we do it</h3>
                      <div className="space-y-3">
                        {service.howWeDoIt.map((item, idx) => (
                          <div key={idx} className="flex items-start space-x-3">
                            <CheckCircle2 className="text-amber-500 flex-shrink-0 mt-0.5" size={18} />
                            <p className="text-slate-600 text-base leading-relaxed">{item}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Business Outcomes */}
                  <div className="p-6 bg-slate-50 border border-slate-200 rounded-xl">
                    <h3 className="text-lg font-bold text-slate-900 mb-4">Business outcomes</h3>
                    <div className="flex flex-wrap gap-2">
                      {service.outcomes.map((outcome, idx) => (
                        <span key={idx} className="px-4 py-2 bg-amber-100 text-amber-700 text-sm font-medium rounded-lg">
                          {outcome}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Image */}
                <div className={!isEven ? 'lg:col-start-1 lg:row-start-1' : ''}>
                  <div className="relative sticky top-32">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="rounded-xl shadow-xl w-full"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* How We Work */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6 leading-tight">
              How We Work With You
            </h2>
            <p className="text-lg text-amber-600 font-medium mb-4">
              A consistent approach, tailored to your context and stage
            </p>
            <p className="text-slate-600 leading-relaxed max-w-3xl mx-auto">
              While every engagement is shaped by the organisation's specific situation, our way of working remains deliberate and disciplined - focused on readiness and execution.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="group">
              <div className="relative overflow-hidden rounded-xl mb-6 h-48">
                <img 
                  src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=600&q=80" 
                  alt="Diagnose"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center mb-2">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <h3 className="text-xl font-bold text-white">Diagnose</h3>
                </div>
              </div>
              <p className="text-slate-600 leading-relaxed">
                We begin with a clear-eyed understanding of your business context, growth stage, and organisational realities. This includes leadership dynamics, people risks, and execution constraints - assessed without assumptions or pre-set models.
              </p>
            </div>

            <div className="group">
              <div className="relative overflow-hidden rounded-xl mb-6 h-48">
                <img 
                  src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=600&q=80" 
                  alt="Design"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center mb-2">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <h3 className="text-xl font-bold text-white">Design</h3>
                </div>
              </div>
              <p className="text-slate-600 leading-relaxed">
                We translate insight into direction. Solutions are co-created in line with your strategy, culture, and operating environment - grounded in what will work, not what is theoretically ideal.
              </p>
            </div>

            <div className="group">
              <div className="relative overflow-hidden rounded-xl mb-6 h-48">
                <img 
                  src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&w=600&q=80" 
                  alt="Deliver"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center mb-2">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <h3 className="text-xl font-bold text-white">Deliver</h3>
                </div>
              </div>
              <p className="text-slate-600 leading-relaxed">
                We move from intent to action. Our focus is on implementation readiness and sustained traction, ensuring recommendations can be executed with clarity, ownership, and pace.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6 leading-tight">
              Approaching a moment where organisational capability matters most?
            </h2>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed max-w-2xl mx-auto">
              We're open to a conversation on readiness, risk, and scale.
            </p>
            <Link
              to="/contact#ready-to-move"
              className="inline-flex items-center px-8 py-4 bg-amber-500 text-slate-900 font-semibold rounded-md hover:bg-amber-400 transition-all duration-200 shadow-lg group"
            >
              Let's Connect
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
